import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  Sheet,
  Table,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Checkbox,
  Textarea,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/joy';
import {
  Plus as AddIcon,
  Code as CodeIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  Copy as CopyIcon,
} from '@phosphor-icons/react';
import { api } from '../../../lib/api';

import type { PublicWidgetConfig, ReviewDesign } from '../../../types/reviews';
import { ReviewWidgetDesignEditor } from './ReviewWidgetDesignEditor';

const designDefaults = { layout_config: {} as ReviewDesign, title: '', show_title: false, show_rating: true, show_response: false, show_reviewer_avatar: false, enable_autoplay: false };

interface ReviewWidget extends Omit<PublicWidgetConfig, 'organization_name'> {
  id: number;
  uuid_public: string;
  name: string;
  widget_type: 'carousel' | 'grid' | 'list' | 'badge' | 'popup' | 'floating';
  platforms: number[];
  min_rating: number;
  reviews_to_show: number;
  is_active: boolean;
  show_date: boolean;
  show_platform: boolean;
  custom_css: string;
  embed_code: string;
  embed_url: string;
  created_at: string;
}

const getEmbedCode = (widget: ReviewWidget): string => {
  return `<iframe src="${widget.embed_url}" width="100%" height="600" frameborder="0"></iframe>`;
};

export const ReviewWidgets: React.FC = () => {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<ReviewWidget | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' as any });
  
  // Form state
  const [formData, setFormData] = useState({
    ...designDefaults,
    name: '',
    widget_type: 'carousel' as ReviewWidget['widget_type'],
    platforms: [] as number[],
    min_rating: 4,
    reviews_to_show: 10,
    is_active: true,
    show_date: true,
    show_platform: true,
    custom_css: '',
  });

  // Fetch widgets
  const { data: widgets, isLoading } = useQuery({
    queryKey: ['review-widgets'],
    queryFn: async () => {
      const response = await api.get<{results?: any[], [key: string]: any}>('/api/marketing/review-widgets/');
      return response.data.results || response.data;
    },
  });

  // Fetch platforms
  const { data: platforms } = useQuery({
    queryKey: ['review-platforms'],
    queryFn: async () => {
      const response = await api.get<{results?: any[], [key: string]: any}>('/api/marketing/review-platforms/');
      return response.data.results || response.data;
    },
  });

  // Create widget mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post('/api/marketing/review-widgets/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-widgets'] });
      setCreateModalOpen(false);
      setSnackbar({ open: true, message: 'Widget created successfully', color: 'success' });
      resetForm();
    },
    onError: (error) => {
      const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
      const first = data && Object.values(data)[0];
      const detail = Array.isArray(first) ? String(first[0]) : typeof first === 'string' ? first : '';
      setSnackbar({
        open: true,
        message: detail ? `Failed to create widget: ${detail}` : 'Failed to create widget',
        color: 'danger',
      });
    },
  });

  // Update widget mutation
  const updateMutation = useMutation({
    mutationFn: async (data: { id: number } & typeof formData) => {
      const response = await api.patch(`/api/marketing/review-widgets/${data.id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-widgets'] });
      setEditModalOpen(false);
      setSnackbar({ open: true, message: 'Widget updated successfully', color: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to update widget', color: 'danger' });
    },
  });

  // Delete widget mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/marketing/review-widgets/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-widgets'] });
      setSnackbar({ open: true, message: 'Widget deleted successfully', color: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete widget', color: 'danger' });
    },
  });

  const resetForm = () => {
    setFormData({
      ...designDefaults,
    name: '',
      widget_type: 'carousel',
      platforms: [],
      min_rating: 4,
      reviews_to_show: 10,
      is_active: true,
      show_date: true,
      show_platform: true,
      custom_css: '',
    });
  };

  const handleCreate = () => {
    if (formData.show_title && !formData.title.trim()) return;
    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (selectedWidget && (!formData.show_title || formData.title.trim())) {
      updateMutation.mutate({ id: selectedWidget.id, ...formData });
    }
  };

  const handleEdit = (widget: ReviewWidget) => {
    setSelectedWidget(widget);
    setFormData({
      ...designDefaults,
      layout_config: widget.layout_config || {},
      title: widget.title || '',
      show_title: !!widget.show_title,
      show_rating: widget.show_rating !== false,
      show_response: !!widget.show_response,
      show_reviewer_avatar: !!widget.show_reviewer_avatar,
      enable_autoplay: !!widget.enable_autoplay,
      name: widget.name,
      widget_type: widget.widget_type,
      platforms: widget.platforms,
      min_rating: widget.min_rating,
      reviews_to_show: widget.reviews_to_show,
      is_active: widget.is_active,
      show_date: widget.show_date,
      show_platform: widget.show_platform,
      custom_css: widget.custom_css || '',
    });
    setEditModalOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this widget?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleShowEmbed = (widget: ReviewWidget) => {
    setSelectedWidget(widget);
    setEmbedModalOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard', color: 'success' });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const noPlatforms = !!platforms && platforms.length === 0;

  return (
    <Box>
      {noPlatforms && (
        <Alert color="warning" variant="soft" sx={{ mb: 2, alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography level="title-sm">No review platforms connected</Typography>
            <Typography level="body-sm">
              Widgets display reviews from your connected profiles (Google Business,
              Facebook, Yelp). You can create a widget now, but it will be empty until
              you connect a profile and sync your reviews.
            </Typography>
          </Box>
          <Button
            size="sm"
            color="warning"
            variant="solid"
            onClick={() => setSearchParams({ tab: 'reviews' }, { replace: true })}
          >
            Connect Google Business Profile
          </Button>
        </Alert>
      )}
      {/* Actions Bar */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button startDecorator={<AddIcon />} onClick={() => setCreateModalOpen(true)}>
          Create Widget
        </Button>
      </Box>

      {/* Widgets Grid */}
      {widgets && widgets.length > 0 ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {widgets.map((widget: ReviewWidget) => (
            <Grid key={widget.id} xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography level="title-md">{widget.name}</Typography>
                      <Chip size="sm" color={widget.is_active ? 'success' : 'neutral'} variant="soft">
                        {widget.is_active ? 'Active' : 'Inactive'}
                      </Chip>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="sm" variant="plain" onClick={() => handleEdit(widget)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="sm" variant="plain" color="danger" onClick={() => handleDelete(widget.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography level="body-sm" sx={{ mb: 2, textTransform: 'capitalize' }}>
                    Type: {widget.widget_type}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Typography level="body-sm">
                      Min Rating: {widget.min_rating}★
                    </Typography>
                    <Typography level="body-sm">
                      Reviews: {widget.reviews_to_show}
                    </Typography>
                  </Box>

                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<CodeIcon />}
                    fullWidth
                    onClick={() => handleShowEmbed(widget)}
                  >
                    Get Embed Code
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Alert color="neutral">No widgets created yet. Create your first widget to get started.</Alert>
      )}

      {/* Widget Types Info */}
      <Card>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>Available Widget Types</Typography>
          
          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Best For</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Carousel</strong></td>
                  <td>Rotating display of recent reviews</td>
                  <td>Homepage hero sections</td>
                </tr>
                <tr>
                  <td><strong>Grid</strong></td>
                  <td>Multiple reviews in a responsive grid</td>
                  <td>Testimonial pages</td>
                </tr>
                <tr>
                  <td><strong>List</strong></td>
                  <td>Vertical list of reviews</td>
                  <td>Sidebar or narrow spaces</td>
                </tr>
                <tr>
                  <td><strong>Badge</strong></td>
                  <td>Compact rating summary</td>
                  <td>Headers or footers</td>
                </tr>
                <tr>
                  <td><strong>Popup</strong></td>
                  <td>Timed popup with recent review</td>
                  <td>Conversion optimization</td>
                </tr>
                <tr>
                  <td><strong>Floating</strong></td>
                  <td>Sticky widget that follows scroll</td>
                  <td>Continuous visibility</td>
                </tr>
              </tbody>
            </Table>
          </Sheet>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 900, width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Create Review Widget</Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl required>
              <FormLabel>Widget Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Homepage Reviews"
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Widget Type</FormLabel>
              <Select
                value={formData.widget_type}
                onChange={(_, value) => value && setFormData({ ...formData, widget_type: value })}
              >
                <Option value="carousel">Carousel</Option>
                <Option value="grid">Grid</Option>
                <Option value="list">List</Option>
                <Option value="badge">Badge</Option>
                <Option value="popup">Popup</Option>
                <Option value="floating">Floating</Option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Platforms</FormLabel>
              <Select
                multiple
                value={formData.platforms}
                onChange={(_, value) => setFormData({ ...formData, platforms: value as number[] })}
                placeholder="Select platforms to include"
              >
                {platforms?.map((platform: any) => (
                  <Option key={platform.id} value={platform.id}>
                    {platform.account_name || platform.platform}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Minimum Rating</FormLabel>
              <Select
                value={formData.min_rating}
                onChange={(_, value) => value && setFormData({ ...formData, min_rating: value })}
              >
                <Option value={1}>1★ and above</Option>
                <Option value={2}>2★ and above</Option>
                <Option value={3}>3★ and above</Option>
                <Option value={4}>4★ and above</Option>
                <Option value={5}>5★ only</Option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Reviews to Show</FormLabel>
              <Input
                type="number"
                value={formData.reviews_to_show}
                onChange={(e) => setFormData({ ...formData, reviews_to_show: parseInt(e.target.value) || 10 })}
                slotProps={{ input: { min: 1, max: 50 } }}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Checkbox
                checked={formData.show_date}
                onChange={(e) => setFormData({ ...formData, show_date: e.target.checked })}
                label="Show Date"
              />
              <Checkbox
                checked={formData.show_platform}
                onChange={(e) => setFormData({ ...formData, show_platform: e.target.checked })}
                label="Show Platform"
              />
              <Checkbox
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                label="Active"
              />
            </Box>

            <ReviewWidgetDesignEditor config={{ ...formData, organization_name: 'Your business' }} onChange={update => setFormData(previous => ({ ...previous, ...update } as typeof formData))} />
            <FormControl>
              <FormLabel>Custom CSS (Optional)</FormLabel>
              <Textarea
                value={formData.custom_css}
                onChange={(e) => setFormData({ ...formData, custom_css: e.target.value })}
                placeholder="Add custom CSS styles..."
                minRows={3}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="outlined" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} loading={createMutation.isPending}>
                Create Widget
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Edit Modal */}
      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 900, width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Edit Review Widget</Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl required>
              <FormLabel>Widget Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Widget Type</FormLabel>
              <Select
                value={formData.widget_type}
                onChange={(_, value) => value && setFormData({ ...formData, widget_type: value })}
              >
                <Option value="carousel">Carousel</Option>
                <Option value="grid">Grid</Option>
                <Option value="list">List</Option>
                <Option value="badge">Badge</Option>
                <Option value="popup">Popup</Option>
                <Option value="floating">Floating</Option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Platforms</FormLabel>
              <Select
                multiple
                value={formData.platforms}
                onChange={(_, value) => setFormData({ ...formData, platforms: value as number[] })}
                placeholder="Select platforms to include"
              >
                {platforms?.map((platform: any) => (
                  <Option key={platform.id} value={platform.id}>
                    {platform.account_name || platform.platform}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Minimum Rating</FormLabel>
              <Select
                value={formData.min_rating}
                onChange={(_, value) => value && setFormData({ ...formData, min_rating: value })}
              >
                <Option value={1}>1★ and above</Option>
                <Option value={2}>2★ and above</Option>
                <Option value={3}>3★ and above</Option>
                <Option value={4}>4★ and above</Option>
                <Option value={5}>5★ only</Option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Reviews to Show</FormLabel>
              <Input
                type="number"
                value={formData.reviews_to_show}
                onChange={(e) => setFormData({ ...formData, reviews_to_show: parseInt(e.target.value) || 10 })}
                slotProps={{ input: { min: 1, max: 50 } }}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Checkbox
                checked={formData.show_date}
                onChange={(e) => setFormData({ ...formData, show_date: e.target.checked })}
                label="Show Date"
              />
              <Checkbox
                checked={formData.show_platform}
                onChange={(e) => setFormData({ ...formData, show_platform: e.target.checked })}
                label="Show Platform"
              />
              <Checkbox
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                label="Active"
              />
            </Box>

            <ReviewWidgetDesignEditor config={{ ...formData, organization_name: 'Your business' }} onChange={update => setFormData(previous => ({ ...previous, ...update } as typeof formData))} />
            <FormControl>
              <FormLabel>Custom CSS (Optional)</FormLabel>
              <Textarea
                value={formData.custom_css}
                onChange={(e) => setFormData({ ...formData, custom_css: e.target.value })}
                placeholder="Add custom CSS styles..."
                minRows={3}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="outlined" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} loading={updateMutation.isPending}>
                Update Widget
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Embed Code Modal */}
      <Modal open={embedModalOpen} onClose={() => setEmbedModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 600, width: '100%' }}>
          <ModalClose />
          <Typography level="h4">Embed Widget</Typography>

          <Typography level="body-md" sx={{ mt: 2, mb: 2 }}>
            Copy and paste this code into your website to display "{selectedWidget?.name}":
          </Typography>

          <FormControl>
            <FormLabel>Inline Embed</FormLabel>
            <Textarea
              value={selectedWidget ? getEmbedCode(selectedWidget) : ''}
              readOnly
              minRows={3}
            />
          </FormControl>

          <Button
            startDecorator={<CopyIcon />}
            onClick={() => selectedWidget && copyToClipboard(getEmbedCode(selectedWidget))}
            sx={{ mt: 2 }}
          >
            Copy to Clipboard
          </Button>
        </ModalDialog>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        color={snackbar.color}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};